import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, computed, onMounted, ComputedRef, onUnmounted } from "vue"
import { useStore } from "vuex"
import { notify } from "@kyvg/vue3-notification"
import useI18n from "@/locales/useI18n"
import AddAttachmentsLayout from "./AddAttachmentsLayout.vue"

type Props = {
  canEdit: boolean,
  canAdd: boolean,
  orderId: number|null
}

type DeleteRequest = {
  displayOrder: number,
  id: number|undefined
}
type ReplaceRequest = DeleteRequest & {
  file: File
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderAttachments',
  props: {
    canEdit: { type: Boolean },
    canAdd: { type: Boolean },
    orderId: {}
  },
  setup(__props: any) {

const store = useStore()
const { t, tc } = useI18n()

const props = __props

const uploadProgress = ref<number|null>(null)


const validAttachmentTypes: ComputedRef<Set<string>> = computed(() => {
  return store.state.attachments.validTypes
})
const allAttachments = computed(() => store.getters['order/allAttachments'])
const canAddMore = computed(() => store.getters['order/canAddMoreAttachments'] && props.canAdd)
const attachmentsHeader = computed(() => {
  if ((allAttachments.value.length === 0) && !props.canEdit) {
    return t("noItemFound", { item: tc("attachment", 2)})
  }
  return tc("attachment", 2)
})


async function handleNewUpload(file: File) {
  await handleProgressBar()
  const isValid = validateFile(file)
  if (isValid) {
    await store.dispatch('order/addNewAttachment', file)
  }
}

async function handleProgressBar() {
  uploadProgress.value = 25
  await new Promise(resolve => setTimeout(resolve, 250))
  uploadProgress.value = 50
  await new Promise(resolve => setTimeout(resolve, 250))
  uploadProgress.value = 75
  await new Promise(resolve => setTimeout(resolve, 250))
  uploadProgress.value = 100
  await new Promise(resolve => setTimeout(resolve, 250))
  uploadProgress.value = null
}

function validateFile(file: File) {
  const MAX_SIZE_MB = 50
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024
  const isTooBig = file.size > MAX_SIZE_BYTES
  const fileType = file.name.split(".").pop()?.toLowerCase() ?? ""
  const isValidType = validAttachmentTypes.value.has(fileType)
  const isValidFile = isValidType && !isTooBig

  if (isTooBig) {
    notify({
      text: t("attachmentFileUploadSizeError"),
      type: "error",
      title: t("error")
    })
  } else if (!isValidType) {
    
    notify({
      text: t("invalidFileType"),
      type: "error",
      title: t("error")
    })
  }
  return isValidFile
}

async function handleDelete(payload: DeleteRequest) {
  await store.dispatch('order/handleDelete', payload)
}

async function handleReplace({ file, displayOrder, id }: ReplaceRequest) {
  if (!file) return
  await handleProgressBar()
  const isValid = validateFile(file)
  
  if (isValid) {
    await store.dispatch('order/handleDelete', { displayOrder, id })
    await store.dispatch('order/handleReplace', { file, displayOrder })
  }
}

onMounted(async () => {
  if (validAttachmentTypes.value.size === 0) {
    await store.dispatch("attachments/fetchValidTypes")
  }
  if (typeof props.orderId === "number") {
    await store.dispatch('order/fetchOrderAttachments', props.orderId)
  }
})

onUnmounted(async () => {
  await store.dispatch('order/clearOrderAttachments')
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AddAttachmentsLayout, {
    "can-edit": _ctx.canEdit,
    "can-add-more": canAddMore.value,
    attachments: allAttachments.value,
    "upload-progress": uploadProgress.value,
    "attachments-header": attachmentsHeader.value,
    onUpload: handleNewUpload,
    onDelete: handleDelete,
    onReplace: handleReplace
  }, null, 8, ["can-edit", "can-add-more", "attachments", "upload-progress", "attachments-header"]))
}
}

})